/**
 * Choosing and explaining a store's IANA timezone.
 *
 * The timezone is not decoration: every daypart, every opening-hours check and
 * every content expiry on that store's screens is evaluated in it, on the device
 * as well as here. So the form offers real zone names rather than a free-text
 * box, and shows what the chosen zone reads as right now — which is how someone
 * catches "Pacific/Auckland" against "Pacific/Chatham" before a screen goes dark
 * forty-five minutes early.
 */

import { zonedParts } from '@pbtv/shared';

/** Offered first, because this fleet is in one country with one sister brand. */
export const SUGGESTED_TIMEZONES: readonly string[] = [
  'Pacific/Auckland',
  'Pacific/Chatham',
  'Australia/Sydney',
  'Australia/Brisbane',
  'Australia/Perth',
  'UTC',
];

export const DEFAULT_TIMEZONE = 'Pacific/Auckland';

interface IntlWithSupportedValues {
  supportedValuesOf?: (key: string) => string[];
}

/**
 * Every zone this browser knows, suggestions first. `Intl.supportedValuesOf` is
 * recent enough that a browser without it is plausible, and its absence costs
 * only the long tail of the list, not the ability to type a zone in.
 */
export function timezoneOptions(): string[] {
  const known = new Set<string>(SUGGESTED_TIMEZONES);

  const intl = Intl as unknown as IntlWithSupportedValues;
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      for (const zone of intl.supportedValuesOf('timeZone')) known.add(zone);
    } catch {
      // An engine that has the method but not the data; the suggestions stand.
    }
  }

  return [...known];
}

/**
 * The zone's offset from UTC at a given instant, in minutes. Derived by reading
 * the wall clock in the zone and comparing it with the instant itself, so
 * daylight saving is handled by the same mechanism the resolver uses rather than
 * by a table that would need maintaining.
 */
export function timezoneOffsetMinutes(timeZone: string, instantMs: number): number | null {
  try {
    const parts = zonedParts(instantMs, timeZone);
    const asUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    return Math.round((asUtc - Math.floor(instantMs / 1000) * 1000) / 60_000);
  } catch {
    return null;
  }
}

export function formatTimezoneOffset(offsetMinutes: number | null): string {
  if (offsetMinutes === null) return 'Unknown zone';
  const sign = offsetMinutes < 0 ? '-' : '+';
  const magnitude = Math.abs(offsetMinutes);
  const hours = String(Math.floor(magnitude / 60)).padStart(2, '0');
  const minutes = String(magnitude % 60).padStart(2, '0');
  return `UTC${sign}${hours}:${minutes}`;
}

/** "Pacific/Auckland — UTC+13:00, 21:45 there now". */
export function describeTimezone(timeZone: string, instantMs: number): string {
  const offset = timezoneOffsetMinutes(timeZone, instantMs);
  if (offset === null) return `${timeZone} — this browser does not know that zone`;

  const parts = zonedParts(instantMs, timeZone);
  const clock = `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
  return `${formatTimezoneOffset(offset)}, ${clock} there now`;
}
